import {
  getProjects as getProjectsFirestore,
  getProject as getProjectFirestore,
  createProject as createProjectFirestore,
  updateProject as updateProjectFirestore,
  deleteProject as deleteProjectFirestore,
  addMeetingToProject as addMeetingToProjectFirestore,
  removeMeetingFromProject as removeMeetingFromProjectFirestore,
  getMeeting
} from '../utils/firestoreUtils';
import { getCurrentUserId, fixAzureUserId } from '../utils/authUtils';
import { doc, updateDoc, serverTimestamp, collection, query, where, orderBy, onSnapshot, getDocs, getDoc, setDoc } from 'firebase/firestore';
import { db } from './firebase/config';

const handleAuthError = async (error, operation, retryFn) => {
  if ((error.code && (error.code === 'permission-denied' || error.code === 'unauthenticated')) ||
      (error.message && (error.message.includes('permission-denied') ||
                         error.message.includes('unauthenticated') ||
                         error.message.includes('Utilisateur non authentifié')))) {

    if (typeof window !== 'undefined' && window.__authContext) {
      try {
        const refreshFn = window.__authContext.refreshFirebaseToken;
        if (typeof refreshFn === 'function') {
          const refreshed = await refreshFn();

          if (refreshed) {
            if (typeof retryFn === 'function') {
              return await retryFn();
            }
          } else {
            localStorage.setItem('auth_refresh_failed', 'true');
          }
        }
      } catch (refreshError) {
      }
    } else {
      localStorage.setItem('auth_refresh_needed', 'true');
    }
  }

  throw error;
};

const projectService = {
  getUserProjects: async (userId) => {
    const retryOperation = async () => projectService.getUserProjects(userId);

    try {
      if (!userId) {
        userId = getCurrentUserId();
      }

      if (!userId) {
        throw new Error('Utilisateur non authentifié');
      }

      const normalizedUserId = fixAzureUserId(userId);

      const projects = await getProjectsFirestore(normalizedUserId);

      return projects;
    } catch (error) {
      console.error('[ProjectService] Erreur récupération projets:', error.message);
      try {
        return await handleAuthError(error, `récupération des projets de l'utilisateur ${userId}`, retryOperation);
      } catch (finalError) {
        return [];
      }
    }
  },

  getProjectById: async (projectId) => {
    const retryOperation = async () => projectService.getProjectById(projectId);

    try {
      return await getProjectFirestore(projectId);
    } catch (error) {
      try {
        return await handleAuthError(error, `récupération du projet ${projectId}`, retryOperation);
      } catch (finalError) {
        return null;
      }
    }
  },

  getProjectWithMeetings: async (projectId) => {
    try {
      const project = await getProjectFirestore(projectId);

      if (!project) {
        return null;
      }

      const meetings = [];
      if (project.meetingIds && project.meetingIds.length > 0) {
        for (const meetingId of project.meetingIds) {
          try {
            const meeting = await getMeeting(meetingId);
            if (meeting) {
              meetings.push(meeting);
            }
          } catch (meetingError) {
          }
        }
      }

      return {
        ...project,
        meetings
      };
    } catch (error) {
      throw error;
    }
  },

  createProject: async (projectData) => {
    try {
      const userId = getCurrentUserId();

      if (!userId) {
        throw new Error('Utilisateur non authentifié');
      }

      const normalizedUserId = fixAzureUserId(userId);

      const newProject = {
        ...projectData,
        userId: normalizedUserId,
        meetingIds: projectData.meetingIds || []
      };

      const projectId = await createProjectFirestore(newProject);

      return { ...newProject, id: projectId };
    } catch (error) {
      console.error('[ProjectService] Erreur création projet:', error.message);
      throw error;
    }
  },

  updateProject: async (projectId, projectData) => {
    try {
      const projectRef = doc(db, 'projects', projectId);

      const updatedData = {
        ...projectData,
        updatedAt: serverTimestamp()
      };

      await updateDoc(projectRef, updatedData);

      return { id: projectId, ...projectData, updatedAt: new Date().toISOString() };
    } catch (error) {
      throw error;
    }
  },

  deleteProject: async (projectId) => {
    try {
      await deleteProjectFirestore(projectId);

      return { success: true, id: projectId };
    } catch (error) {
      throw error;
    }
  },

  addMeetingToProject: async (projectId, meetingId) => {
    try {
      await addMeetingToProjectFirestore(projectId, meetingId);

      const meetingRef = doc(db, 'meetings', meetingId);
      await updateDoc(meetingRef, {
        projectId: projectId,
        updatedAt: serverTimestamp()
      });

      return { success: true };
    } catch (error) {
      throw error;
    }
  },

  removeMeetingFromProject: async (projectId, meetingId) => {
    try {
      await removeMeetingFromProjectFirestore(projectId, meetingId);

      const meetingRef = doc(db, 'meetings', meetingId);
      await updateDoc(meetingRef, {
        projectId: null,
        updatedAt: serverTimestamp()
      });

      return { success: true };
    } catch (error) {
      throw error;
    }
  },

  onUserProjects: (userId, callback) => {
    try {
      const normalizedUserId = fixAzureUserId(userId);
      const projectsRef = collection(db, 'projects');
      const q = query(
        projectsRef,
        where('userId', '==', normalizedUserId),
        orderBy('createdAt', 'desc')
      );

      return onSnapshot(q, (querySnapshot) => {
        const projects = [];
        querySnapshot.forEach(doc => {
          const data = doc.data();
          projects.push({
            id: doc.id,
            ...data,
            createdAt: data.createdAt?.toDate() || new Date(),
            updatedAt: data.updatedAt?.toDate() || new Date()
          });
        });

        callback(projects);
      }, (error) => {
        // Détecter les erreurs de connexion bloquée par extension navigateur
        if (error.message && error.message.includes('ERR_BLOCKED_BY_CLIENT')) {
          console.error('🚫 Firestore bloqué par une extension navigateur (AdBlock/uBlock). Veuillez désactiver votre bloqueur de publicités pour ce site.');
        }
        callback([]);
      });
    } catch (error) {
      console.error('❌ Erreur lors de l\'écoute des projets:', error);
      callback([]);
      return () => {};
    }
  },

  onProjectById: (projectId, callback) => {
    try {
      const projectRef = doc(db, 'projects', projectId);

      return onSnapshot(projectRef, (docSnapshot) => {
        if (docSnapshot.exists()) {
          const data = docSnapshot.data();
          callback({
            id: docSnapshot.id,
            ...data,
            createdAt: data.createdAt?.toDate() || new Date(),
            updatedAt: data.updatedAt?.toDate() || new Date()
          });
        } else {
          callback(null);
        }
      }, (error) => {
        callback(null);
      });
    } catch (error) {
      callback(null);
      return () => {};
    }
  },

  getAllProjects: async (limitCount = 100) => {
    try {
      const projectsRef = collection(db, 'projects');
      const q = query(
        projectsRef,
        orderBy('createdAt', 'desc')
      );

      const querySnapshot = await getDocs(q);

      const projects = querySnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          createdAt: data.createdAt?.toDate() || new Date(),
          updatedAt: data.updatedAt?.toDate() || new Date()
        };
      });

      return projects;
    } catch (error) {
      console.error('[ProjectService] Erreur récupération de tous les projets:', error.message);
      return [];
    }
  },

  onAllProjects: (callback) => {
    try {
      const projectsRef = collection(db, 'projects');
      const q = query(
        projectsRef,
        orderBy('createdAt', 'desc')
      );

      return onSnapshot(q, (querySnapshot) => {
        const projects = [];
        querySnapshot.forEach(doc => {
          const data = doc.data();
          projects.push({
            id: doc.id,
            ...data,
            createdAt: data.createdAt?.toDate() || new Date(),
            updatedAt: data.updatedAt?.toDate() || new Date()
          });
        });

        callback(projects);
      }, (error) => {
        if (error.message && error.message.includes('ERR_BLOCKED_BY_CLIENT')) {
          console.error('🚫 Firestore bloqué par une extension navigateur (AdBlock/uBlock). Veuillez désactiver votre bloqueur de publicités pour ce site.');
        }
        callback([]);
      });
    } catch (error) {
      console.error('❌ Erreur lors de l\'écoute de tous les projets:', error);
      callback([]);
      return () => {};
    }
  },

  getProjectSummary: async (projectId) => {
    try {
      const summaryRef = doc(db, 'projectSummaries', projectId);
      const summaryDoc = await getDoc(summaryRef);

      if (summaryDoc.exists()) {
        const data = summaryDoc.data();
        return {
          id: summaryDoc.id,
          ...data,
          createdAt: data.createdAt?.toDate() || new Date(),
          updatedAt: data.updatedAt?.toDate() || new Date()
        };
      }

      return null;
    } catch (error) {
      console.error('[ProjectService] Erreur récupération résumé projet:', error.message);
      return null;
    }
  },

  onProjectSummary: (projectId, callback) => {
    try {
      const summaryRef = doc(db, 'projectSummaries', projectId);

      return onSnapshot(summaryRef, (docSnapshot) => {
        if (docSnapshot.exists()) {
          const data = docSnapshot.data();
          callback({
            id: docSnapshot.id,
            ...data,
            createdAt: data.createdAt?.toDate() || new Date(),
            updatedAt: data.updatedAt?.toDate() || new Date()
          });
        } else {
          callback(null);
        }
      }, (error) => {
        console.error('[ProjectService] Erreur écoute résumé projet:', error.message);
        callback(null);
      });
    } catch (error) {
      console.error('[ProjectService] Erreur onProjectSummary:', error);
      callback(null);
      return () => {};
    }
  },

  generateProjectSummary: async (projectId) => {
    try {
      const userId = getCurrentUserId();

      if (!userId) {
        throw new Error('Utilisateur non authentifié');
      }

      const summaryService = (await import('./summaryService')).default;

      const project = await getProjectFirestore(projectId);

      if (!project) {
        throw new Error('Projet non trouvé');
      }

      if (!project.meetingIds || project.meetingIds.length === 0) {
        throw new Error('Le projet ne contient aucune réunion');
      }

      const meetingSummaries = [];

      for (const meetingId of project.meetingIds) {
        try {
          const meeting = await getMeeting(meetingId);

          if (meeting && meeting.transcriptionId) {
            const summary = await summaryService.getSummaryByTranscriptionId(meeting.transcriptionId, { isAdmin: false });

            if (summary && summary.text) {
              meetingSummaries.push({
                meetingTitle: meeting.title || 'Sans titre',
                meetingDate: meeting.date || meeting.createdAt,
                summary: summary.text
              });
            }
          }
        } catch (meetingError) {
          console.warn(`[ProjectService] Erreur récupération résumé réunion ${meetingId}:`, meetingError.message);
        }
      }

      if (meetingSummaries.length === 0) {
        throw new Error('Aucun résumé de réunion disponible pour ce projet');
      }

      let consolidatedText = `# Résumé du projet: ${project.name}\n\n`;

      if (project.description) {
        consolidatedText += `## Description\n${project.description}\n\n`;
      }

      consolidatedText += `## Résumés des réunions (${meetingSummaries.length})\n\n`;

      meetingSummaries.forEach((item, index) => {
        consolidatedText += `### ${index + 1}. ${item.meetingTitle}\n`;
        if (item.meetingDate) {
          consolidatedText += `**Date:** ${item.meetingDate}\n\n`;
        }
        consolidatedText += `${item.summary}\n\n`;
        consolidatedText += '---\n\n';
      });

      const summaryRef = doc(db, 'projectSummaries', projectId);
      await setDoc(summaryRef, {
        projectId: projectId,
        userId: fixAzureUserId(userId),
        text: consolidatedText,
        meetingCount: meetingSummaries.length,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });

      return {
        id: projectId,
        projectId: projectId,
        userId: fixAzureUserId(userId),
        text: consolidatedText,
        meetingCount: meetingSummaries.length,
        createdAt: new Date(),
        updatedAt: new Date()
      };
    } catch (error) {
      console.error('[ProjectService] Erreur génération résumé projet:', error.message);
      throw error;
    }
  }
};

export default projectService;
